import { useEffect, useState } from 'react';
import constants from '../../common/constants';
import { useAttachmentManagementService } from '../../contexts/AttachmentManagementServiceContext';
import { classifyScanVerdict } from '../../services/AttachmentManagementService';

/** Presentation state for the attachment download page. */
export interface AttachmentDownloadPageViewModel {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly errorMessage: string;
  readonly downloadUrl: string | undefined;
  readonly filename: string | undefined;
}

/** Fetches a pre-signed download URL and auto-triggers the browser download. */
export function useAttachmentDownloadPageViewModel(
  contentId: string,
  attachmentId: string,
): AttachmentDownloadPageViewModel {
  const attachmentManagementService = useAttachmentManagementService();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const [filename, setFilename] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Track unmount so late responses don't write to stale state.
    let isCancelled = false;

    async function fetchDownloadUrl() {
      try {
        const attachment = await attachmentManagementService.getAttachment(contentId, attachmentId);

        if (isCancelled) return;

        const scanVerdict = classifyScanVerdict(attachment);

        // Refuse infected files outright — a signed URL may still be present.
        if (scanVerdict === 'infected') {
          setIsLoading(false);
          setIsError(true);
          setErrorMessage(constants.ATTACHMENT_DOWNLOAD_INFECTED);
          return;
        }

        // Refuse files the scanner has not cleared yet.
        if (scanVerdict === 'pending') {
          setIsLoading(false);
          setIsError(true);
          setErrorMessage(constants.ATTACHMENT_DOWNLOAD_NOT_SCANNED);
          return;
        }

        // Surface a friendly error when the backend returns no signed URL.
        if (!attachment.url) {
          setIsLoading(false);
          setIsError(true);
          setErrorMessage('This attachment does not have a download URL available.');
          return;
        }

        // Expose the URL/filename for the fallback link, then auto-trigger the browser download.
        setIsLoading(false);
        setDownloadUrl(attachment.url);
        setFilename(attachment.filename ? decodeURIComponent(attachment.filename) : undefined);
        window.open(attachment.url, '_self');
      } catch (error) {
        if (isCancelled) return;

        // Preserve the original error text so the user can act on it (e.g. retry on network failure).
        const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
        setIsLoading(false);
        setIsError(true);
        setErrorMessage(`Failed to retrieve the download link. ${message}`);
      }
    }

    fetchDownloadUrl();

    return () => {
      isCancelled = true;
    };
  }, [contentId, attachmentId, attachmentManagementService]);

  return { isLoading, isError, errorMessage, downloadUrl, filename };
}
